import { useState, type ReactElement } from "react";
import type { GmRole, StaffType } from "odyssey-shared";
import { Button, Dropdown, EmptyState } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import type { StaffRow } from "./staff-client.ts";
import type { StaffSnapshot } from "./staff.ts";

/**
 * The Moderation window's Staff tab, drawn for a lead alone (ADR-112 decision 5.5, P1.9-T207): the staff accounts with their
 * roles, the badge each shows and the designation a lead gave it, and for the account chosen the types it holds now, to set
 * the designation or clear it. The tab is only drawn for an offer that carries the word `staff`; the gateway's routes refuse
 * every other account whatever the page sends, and the page words what they answer.
 */

/**
 * A staff type's name, as a badge's hover line says it.
 *
 * @param type The type.
 * @returns The name.
 */
export function typeName(type: StaffType): string {
  return type === "guide_lead" ? say("staff.type.guideLead") : say(`staff.type.${type}`);
}

/**
 * A role's name.
 *
 * @param role The role.
 * @returns The name.
 */
export function roleName(role: GmRole): string {
  switch (role) {
    case "guide_lead":
      return say("moderation.window.staff.roleNames.guideLead");
    case "support_lead":
      return say("moderation.window.staff.roleNames.supportLead");
    case "event_lead":
      return say("moderation.window.staff.roleNames.eventLead");
    case "builder_lead":
      return say("moderation.window.staff.roleNames.builderLead");
    case "designer_lead":
      return say("moderation.window.staff.roleNames.designerLead");
    default:
      return say(`moderation.window.staff.roleNames.${role}`);
  }
}

/**
 * The sentence for why a change was not saved, or null for none.
 *
 * @param snapshot The tab's snapshot.
 * @returns The sentence.
 */
function faultOf(snapshot: StaffSnapshot): string | null {
  return snapshot.error === null ? null : say(`moderation.window.staff.faults.${snapshot.error}`);
}

/**
 * The chosen account's panel: the types it holds now, which one to show, and Set and Clear.
 *
 * @param props The `row`, whether a change is `busy`, and `onDesignate`.
 * @returns The panel.
 */
function StaffDetail({ row, busy, onDesignate }: { readonly row: StaffRow; readonly busy: boolean; readonly onDesignate: (type: StaffType | null) => void }): ReactElement {
  const [choice, setChoice] = useState<StaffType>(row.designated ?? row.wears);
  const held = row.types.includes(choice) ? choice : (row.types[0] ?? row.wears);
  return (
    <div className="ot-moderation__staff-detail ot-panel ot-ui-frame-panel" data-moderation="staff-detail">
      <span className="ot-moderation__accent">{row.family}</span>
      <div className="ot-moderation__staff-pick-row">
        <span className="ot-moderation__muted ot-moderation__label">{say("moderation.window.staff.wornLabel")}</span>
        <Dropdown label={say("moderation.window.staff.wornLabel")} options={row.types.map((type) => ({ value: type, label: typeName(type) }))} value={held} onChange={setChoice} />
      </div>
      <p className="ot-moderation__muted ot-moderation__small">{say("moderation.window.staff.holds", { family: row.family })}</p>
      <div className="ot-moderation__act-row">
        <Button variant="primary" disabled={busy || held === row.designated} onClick={() => onDesignate(held)}>
          {say("moderation.window.staff.set")}
        </Button>
        <Button disabled={busy || row.designated === null} onClick={() => onDesignate(null)}>
          {say("moderation.window.staff.clear")}
        </Button>
      </div>
    </div>
  );
}

/**
 * The Staff tab.
 *
 * @param props The model's `snapshot`; `onSelect`, run with a family when its row is pressed; `onDesignate`, run with the type
 *   to show or null to clear; and `onRefresh`, run with Read again.
 * @returns The tab's body.
 */
export function StaffTab({ snapshot, onSelect, onDesignate, onRefresh }: { readonly snapshot: StaffSnapshot; readonly onSelect: (family: string) => void; readonly onDesignate: (type: StaffType | null) => void; readonly onRefresh: () => void }): ReactElement {
  if (snapshot.rows.length === 0 && (snapshot.state === "failed" || snapshot.state === "rate" || snapshot.state === "session" || snapshot.state === "forbidden")) {
    return (
      <div className="ot-moderation__records" data-moderation="staff">
        <p className="ot-moderation__refused" role="alert" data-moderation="list-error">
          {say(`moderation.window.staff.readFaults.${snapshot.state}`)}
        </p>
        <div className="ot-moderation__act-row">
          <Button onClick={onRefresh}>{say("moderation.window.staff.readAgain")}</Button>
        </div>
      </div>
    );
  }
  if (snapshot.rows.length === 0) {
    return snapshot.state === "ok" ? (
      <EmptyState glyph="user" title={say("moderation.window.staff.heading")}>
        {say("moderation.window.staff.empty")}
      </EmptyState>
    ) : (
      <p className="ot-moderation__muted ot-moderation__small" role="status" data-moderation="staff">
        {say("moderation.window.staff.reading")}
      </p>
    );
  }
  const chosen = snapshot.rows.find((row) => row.family === snapshot.selected) ?? null;
  const fault = faultOf(snapshot);
  const saved = snapshot.saved === null ? null : snapshot.saved.type === null ? say("moderation.window.staff.cleared", { family: snapshot.saved.family }) : say("moderation.window.staff.saved", { family: snapshot.saved.family, type: typeName(snapshot.saved.type) });
  return (
    <div className="ot-moderation__records" data-moderation="staff">
      <p className="ot-moderation__accent">{say("moderation.window.tabCounted", { tab: { k: "moderation.window.staff.heading" }, count: String(snapshot.rows.length) })}</p>
      {fault === null ? null : (
        <p className="ot-moderation__refused" role="alert" data-moderation="staff-fault">
          {fault}
        </p>
      )}
      {saved === null ? null : (
        <p className="ot-moderation__line" role="status" data-moderation="staff-saved">
          {saved}
        </p>
      )}
      {snapshot.state === "failed" || snapshot.state === "rate" || snapshot.state === "session" || snapshot.state === "forbidden" ? (
        <p className="ot-moderation__refused" role="alert" data-moderation="list-error">
          {say(`moderation.window.staff.readFaults.${snapshot.state}`)}
        </p>
      ) : null}
      {snapshot.more ? <p className="ot-moderation__muted ot-moderation__small" data-moderation="staff-more">{say("moderation.window.staff.more", { count: String(snapshot.rows.length) })}</p> : null}
      <div role="table" aria-label={say("moderation.window.staff.heading")} className="ot-moderation__table ot-panel ot-ui-frame-panel" data-moderation="staff-rows">
        <div role="row" className="ot-moderation__staff-row ot-moderation__history-head">
          {[say("moderation.window.staff.family"), say("moderation.window.staff.roles"), say("moderation.window.staff.wears"), say("moderation.window.staff.designated")].map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {snapshot.rows.map((row) => (
          <div key={row.family} role="row" aria-selected={row.family === snapshot.selected} className={row.family === snapshot.selected ? "ot-moderation__staff-row ot-moderation__staff-row--on" : "ot-moderation__staff-row"} data-moderation="staff-row">
            <span role="cell" className="ot-moderation__clip">
              <button type="button" className="ot-moderation__staff-name" onClick={() => onSelect(row.family)}>
                {row.family}
              </button>
            </span>
            <span role="cell" className="ot-moderation__muted ot-moderation__small">
              {row.roles.map(roleName).join(", ")}
            </span>
            <span role="cell">
              {row.hidden ? say("moderation.window.staff.hidden", { type: typeName(row.wears) }) : typeName(row.wears)}
            </span>
            <span role="cell" className={row.designated === null ? "ot-moderation__muted ot-moderation__small" : "ot-moderation__clip"}>
              {row.designated === null ? say("moderation.window.staff.none") : typeName(row.designated)}
            </span>
          </div>
        ))}
      </div>
      {chosen === null ? (
        <p className="ot-moderation__muted ot-moderation__small">{say("moderation.window.staff.pick")}</p>
      ) : (
        <StaffDetail key={chosen.family + String(chosen.designated)} row={chosen} busy={snapshot.saving} onDesignate={onDesignate} />
      )}
    </div>
  );
}
